import { useMemo } from "react";
import { toast } from "sonner";
import { useFormBuilderStore } from "@/stores/form-builder-store";
import { useFieldMutations } from "@/hooks/use-form-mutations";
import { FieldCard } from "@/components/forms/FieldCard";
import { FieldEditor } from "@/components/forms/FieldEditor";
import { NewFieldEditor } from "@/components/forms/NewFieldEditor";
import { FieldTypePicker } from "@/components/forms/FieldTypePicker";
import type { FormWithFields } from "@/types/forms";

interface FieldListProps {
  form: FormWithFields;
}

export function FieldList({ form }: FieldListProps) {
  const editingFieldId = useFormBuilderStore((s) => s.editingFieldId);
  const newFieldType = useFormBuilderStore((s) => s.newFieldType);
  const startEditField = useFormBuilderStore((s) => s.startEditField);
  const cancelEditField = useFormBuilderStore((s) => s.cancelEditField);
  const startNewField = useFormBuilderStore((s) => s.startNewField);
  const cancelNewField = useFormBuilderStore((s) => s.cancelNewField);
  const { removeField } = useFieldMutations(form.id);

  async function handleDeleteField(fieldId: string) {
    try {
      await removeField.mutateAsync(fieldId);
      if (editingFieldId === fieldId) cancelEditField();
      toast.success("Field deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete field");
    }
  }

  const sortedFields = useMemo(
    () => [...form.fields].sort((a, b) => a.position - b.position),
    [form.fields]
  );

  return (
    <div className="space-y-2">
      {sortedFields.map((field, index) =>
        editingFieldId === field.id ? (
          <FieldEditor
            key={field.id}
            field={field}
            formId={form.id}
            onClose={cancelEditField}
          />
        ) : (
          <FieldCard
            key={field.id}
            field={field}
            index={index}
            onEdit={() => startEditField(field.id)}
            onDelete={() => handleDeleteField(field.id)}
          />
        )
      )}
      {newFieldType ? (
        <NewFieldEditor
          type={newFieldType}
          formId={form.id}
          onClose={cancelNewField}
        />
      ) : (
        <FieldTypePicker
          onSelect={startNewField}
          disabled={false}
        />
      )}
    </div>
  );
}
